import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2, Hammer, BadgePercent } from "lucide-react";
import { useVeteranDiscount } from "@/lib/veteran";

export default function EstimateRequest() {
  const { slug } = useParams();
  const [cfg, setCfg] = useState(undefined); // undefined=loading, null=not found
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", address: "", timeline: "", details: "", prior_service: null, service_status: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const vetCfg = useVeteranDiscount();

  useEffect(() => {
    api.get(`/estimate/${slug}`).then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, [slug]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim() || !form.email.trim()) { setErr("Please add your name and email."); return; }
    setBusy(true);
    try {
      await api.post(`/estimate/${slug}`, form);
      setDone(true);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  if (cfg === undefined) return <div className="min-h-screen bg-navy flex items-center justify-center"><Loader2 className="animate-spin text-gold" /></div>;
  if (cfg === null) return <div className="min-h-screen bg-navy flex items-center justify-center text-gray-400" data-testid="estimate-notfound">This estimate form isn't available.</div>;

  const ic = "w-full bg-navy-800 border border-white/15 text-white p-3 text-sm focus:border-gold focus:outline-none";

  return (
    <div className="min-h-screen bg-navy text-white flex items-center justify-center p-4" data-testid="estimate-page">
      <div className="w-full max-w-lg bg-navy-800 border border-white/10 p-8">
        {done ? (
          <div className="text-center py-10" data-testid="estimate-success">
            <CheckCircle2 className="mx-auto text-green-400 mb-4" size={48} />
            <h1 className="font-heading text-2xl mb-2">Request received</h1>
            <p className="text-gray-400 text-sm">Thanks, {form.name.split(" ")[0]}! {cfg.business_name} will be in touch shortly.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] mb-2"><Hammer size={14} /> Free Estimate</div>
            <h1 className="font-heading text-3xl mb-1">{cfg.business_name}</h1>
            <p className="text-gray-400 text-sm mb-6">Tell us about your project and we'll get right back to you.{cfg.services ? ` Services: ${cfg.services}.` : ""}</p>
            <form onSubmit={submit} className="space-y-3" data-testid="estimate-form">
              <input className={ic} placeholder="Your name *" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="lead-name" />
              <div className="grid grid-cols-2 gap-3">
                <input className={ic} placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="lead-email" />
                <input className={ic} placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="lead-phone" />
              </div>
              <input className={ic} placeholder="What do you need? (e.g. roof repair)" value={form.service} onChange={(e) => set("service", e.target.value)} data-testid="lead-service" />
              <div className="grid grid-cols-2 gap-3">
                <input className={ic} placeholder="Address / area" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="lead-address" />
                <input className={ic} placeholder="Timeline (e.g. ASAP)" value={form.timeline} onChange={(e) => set("timeline", e.target.value)} data-testid="lead-timeline" />
              </div>
              <textarea rows={3} className={ic} placeholder="Project details" value={form.details} onChange={(e) => set("details", e.target.value)} data-testid="lead-details" />
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Prior military service?</label>
                <div className="flex gap-2" data-testid="lead-prior-service">
                  <button type="button" onClick={() => set("prior_service", true)} data-testid="lead-prior-yes"
                    className={`px-6 py-2.5 text-sm border transition-colors ${form.prior_service === true ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>Yes</button>
                  <button type="button" onClick={() => { set("prior_service", false); set("service_status", ""); }} data-testid="lead-prior-no"
                    className={`px-6 py-2.5 text-sm border transition-colors ${form.prior_service === false ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>No</button>
                </div>
                {form.prior_service === true && (
                  <div className="mt-3" data-testid="lead-service-status">
                    <label className="block text-sm text-gray-400 mb-1.5">Which best describes you?</label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button type="button" onClick={() => set("service_status", "transitioning")} data-testid="lead-status-transitioning"
                        className={`px-5 py-2.5 text-sm border transition-colors ${form.service_status === "transitioning" ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>Currently serving / transitioning</button>
                      <button type="button" onClick={() => set("service_status", "veteran")} data-testid="lead-status-veteran"
                        className={`px-5 py-2.5 text-sm border transition-colors ${form.service_status === "veteran" ? "bg-gold text-navy border-gold" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>Already separated / retired</button>
                    </div>
                    {vetCfg?.enabled && vetCfg.percent > 0 && (
                      <div className="mt-3 flex items-start gap-2 border border-gold/30 bg-gold/[0.07] px-3 py-2.5 text-sm text-gray-200" data-testid="lead-veteran-note">
                        <BadgePercent size={16} className="text-gold shrink-0 mt-0.5" />
                        <span>Thank you for your service — you qualify for <span className="text-gold font-semibold">{vetCfg.percent}% off</span>. We'll apply it to your quote automatically.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {err && <div className="text-red-400 text-sm" data-testid="estimate-error">{err}</div>}
              <button type="submit" disabled={busy} data-testid="estimate-submit"
                className="w-full bg-gold text-navy py-3 font-semibold hover:bg-gold-light transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {busy ? <Loader2 className="animate-spin" size={16} /> : null} Request My Free Estimate
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
